import type { FunctionComponent, MouseEvent } from 'react';
import { useCallback } from 'react';

import { useMedia } from '../../hooks/useMedia';
import { Logger } from '../../logger/Logger';
import { CoreLobbyMessageController, hostInfoMessage } from '../../messages/CoreLobbyMessageController';
import { L10nText } from '../../services/uiTranslations/reactBindings/L10nText';
import { useVideoElement } from '../../services/video/reactBindings/hooks';
import { useCoreDispatch } from '../../store/CoreStore';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useGameStateSlice } from '../../store/slices/game/selectors';
import { gameStateActions } from '../../store/slices/game/slice';
import { useHeaderSlice } from '../../store/slices/header/selectors';
import { headerActions } from '../../store/slices/header/slice';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { settingsActions } from '../../store/slices/settings/slice';
import { IconButton, IconButtonSize, Icons } from '../controls/iconButton/IconButton';
import { TrafficLightConnected } from '../game/trafficLight/TrafficLightConnected';

import { BackToGame } from './BackToGame';
import { BACK_TO_GAME_SPECIAL_SIZE_RULE } from './constants';

export const BackToGameConnected: FunctionComponent = () => {
  const dispatch = useCoreDispatch();
  const videoElement = useVideoElement();
  const masterVolumeValue = useSettingsSlice.getMasterChannelValue();

  const tableName = useConfigSlice.tableName();
  const isLobbyOpen = useHeaderSlice.getIsMiniLobbyOpen();
  const isGameSuspended = useGameStateSlice.getIsGameSuspended();
  const isGameAbandoned = useGameStateSlice.getIsGameAbandoned();
  const lobbyVersion = useConfigSlice.casinoConfig()?.lobbyVersion;
  const poster = useGameStateSlice.getVideoPoster();
  const isBackToGameHidden = useHeaderSlice.getIsBackToGameHidden();

  const isSmall = useMedia(BACK_TO_GAME_SPECIAL_SIZE_RULE);

  const handleClick = useCallback((): void => {
    Logger.logEvent('backToGameClick');
    dispatch(headerActions.setNavRoute([]));
    dispatch(headerActions.setMiniLobbyOpen(false));
  }, [dispatch]);

  const handleCloseClick = useCallback((): void => {
    Logger.logEvent('backToGameClose');
    CoreLobbyMessageController.post(hostInfoMessage({ id: null }));
    dispatch(gameStateActions.setGameSuspended(true));
  }, [dispatch]);

  const handleSoundToggle = useCallback(
    (event?: MouseEvent<HTMLButtonElement>): void => {
      event?.stopPropagation();
      dispatch(settingsActions.toggleMasterVolume());
    },
    [dispatch]
  );

  if (lobbyVersion === '1') return;
  return (
    <BackToGame
      actionLabel={<L10nText tk="LOBBY_BACK_TO_GAME" />}
      isHidden={isBackToGameHidden}
      isOpen={isLobbyOpen && !isGameSuspended && !isGameAbandoned}
      name={tableName ? <L10nText tk={tableName} /> : undefined}
      onClick={handleClick}
      onClose={handleCloseClick}
      poster={poster}
      trafficLight={<TrafficLightConnected line />}
      videoElement={videoElement}
      secondaryActions={
        <IconButton
          icon={masterVolumeValue ? Icons.SoundOn : Icons.SoundOff}
          size={isSmall ? IconButtonSize.SMALL : IconButtonSize.TINY}
          ghost
          onClick={handleSoundToggle}
        />
      }
      nowPlayingLabel={<L10nText tk="LOBBY_NOW_PLAYING" />}
    />
  );
};
